import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/ui/button';
import { Input, Field } from '@/ui/input';
import { MicroLabel } from '@/ui/micro-label';
import { ErrorState } from '@/ui/error-state';
import { Wordmark } from '@/domain/wordmark';

/**
 * Where an invite link lands. The operator creates the vendor, copies the one-time link and hands
 * it over; this is the screen at the other end of it, and the only place a vendor account gets a
 * password before it can sign in.
 *
 * The token is read *before* the form renders, for two reasons. It tells the invitee they have the
 * right link -- their own email, their own organisation -- and it means a link that has already
 * been used, revoked or expired says so instead of presenting a password field that cannot
 * succeed. There is no session here and there cannot be one: the account has no password yet.
 */
export function VendorJoin() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const invite = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api<{ email: string; organization_name: string | null }>(`/invites/${encodeURIComponent(token)}`),
    retry: false
  });

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    // Checked here as well as by the server because the server never sees the second field: it is
    // the only thing standing between a typo and an account nobody can sign in to.
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const { email } = await api<{ accepted: boolean; email: string | null }>('/accept-invite', {
        method: 'POST',
        realm: 'vendor',
        body: JSON.stringify({ token, password })
      });
      // Straight to sign-in rather than straight in: the session is created by logging in, and MFA
      // enrolment is the next thing the guard will ask for either way.
      navigate('/v/login', { state: { email: email ?? invite.data?.email } });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not complete registration.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-screen place-items-center bg-background px-4 py-12">
      <div className="w-full max-w-[352px]">
        <div className="grid justify-items-center gap-3">
          <Wordmark height={16} />
          <span className="rounded-[3px] border border-border px-2 py-1 font-mono text-[10px] tracking-[0.12em] text-muted uppercase">
            Vendor portal
          </span>
        </div>

        {invite.isPending ? (
          <p className="mt-8 text-center text-[13px] text-muted">Checking your invite…</p>
        ) : invite.isError ? (
          <ErrorState
            className="mt-8"
            error={invite.error}
            title="This invite link is not usable"
            onRetry={() => void invite.refetch()}
          />
        ) : (
          <form onSubmit={submit} className="mt-8 grid gap-4 rounded border border-border bg-card px-8 py-7">
            <div className="grid gap-1.5">
              <h1 className="text-[22px] font-medium text-foreground">Set your password</h1>
              <p className="text-[13px] leading-relaxed text-muted">
                You were invited to {invite.data.organization_name ?? 'an organization'} as{' '}
                <span className="font-mono text-foreground">{invite.data.email}</span>.
              </p>
            </div>

            <Field label="Password" htmlFor="password">
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={event => setPassword(event.target.value)}
                required
              />
            </Field>

            <Field label="Confirm password" htmlFor="confirm">
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={event => setConfirm(event.target.value)}
                required
              />
            </Field>

            {error ? <p className="text-[13px] text-danger">{error}</p> : null}

            <Button type="submit" disabled={busy} className="mt-1 w-full">
              {busy ? 'Registering…' : 'Register and continue'}
            </Button>

            <p className="text-center text-[11px] leading-relaxed text-muted">
              You will be asked to set up two-factor authentication when you first sign in.
            </p>
          </form>
        )}

        <MicroLabel size="xs" caps={false} className="mt-6 block text-center">
          miraxis.ai · vendor support: partners@miraxis.ai
        </MicroLabel>
      </div>
    </div>
  );
}
